/**
 * Centralized accent token set for entity cards.
 *
 * Each entity type is assigned one accent token, and the accent identifies the
 * type at a glance.
 *
 * The rule changed with the Semi Design migration. It used to be "the accent
 * drives EVERY card surface": the container got an accent gradient plus a 2px
 * accent border, and the footer and divider were accent-tinted too. With five
 * types on one board that produced five differently-tinted candy panels, and
 * any Semi component dropped inside a card (Button, Input, Select) clashed with
 * the tint it was sitting on.
 *
 * The rule now is: **chrome is neutral, the accent only marks identity.**
 *   - Neutral, token-driven: container surface, border, footer, divider. These
 *     come from Semi's `--semi-color-*` variables, so a card is literally the
 *     same surface as the Semi components inside it, in both light and dark.
 *   - Accent-colored: a 2px top edge, tags, the enabled toggle, links and icon
 *     chips. Small, high-signal areas.
 *
 * The `primary` accent resolves to the shared brand ramp (`--brand-*`, defined
 * in index.css) rather than Tailwind's stock `purple-*`. Those were two
 * different purples -- `.btn-primary` rendered #7a00cc while every card next to
 * it rendered #9333ea -- which read as a rendering bug. There is now one purple
 * across Tailwind classes, these accents, and Semi's own components.
 */

/** Class fragments for every accent-driven surface of a card. */
export interface AccentClasses {
  /** Outer container: neutral surface + border, with a 2px accent top edge. */
  container: string;
  /** Footer bar: border color + background tint. Neutral for every accent. */
  footer: string;
  /** Thin vertical divider between footer status items. Neutral. */
  divider: string;
  /** Toggle switch background when enabled. */
  toggleOn: string;
  /** Tag pill background + text. */
  tag: string;
  /** Interactive accent for tool-count buttons, links, icon chips. */
  interactive: string;
  /** Soft background chip behind an icon (e.g. the tool-count icon). */
  iconChip: string;
}

/**
 * The neutral card chrome, shared by every accent.
 *
 * `app-surface` is the raised step above the page background (white in light
 * mode, rgb(35,36,41) in dark) -- that one step is what makes a card read as a
 * card without needing a heavy border or shadow. It is an app-level token, not
 * Semi's `bg-*`, because Semi's stops are only an elevation ramp in dark mode
 * and are all white in light mode; see the comment in index.css.
 * The border and footer tints do come from Semi's variables, which already swap
 * with the theme, so they carry no `dark:` variant -- one value to keep correct.
 */
const SURFACE = 'bg-app-surface border border-semi-color-border';
const FOOTER = 'border-semi-color-border bg-semi-color-fill-0';
const DIVIDER = 'bg-semi-color-border';

/** The accent-colored parts. Everything else is assembled from the neutrals. */
interface AccentSpec {
  /**
   * The card's identity edge, expressed as a 2px top *border* rather than an
   * absolutely positioned bar: a border follows the rounded corners for free,
   * and an overlaid bar would need `overflow-hidden` on the card, which would
   * clip the dropdowns and popovers the cards open (CardFooter rounds its own
   * bottom corners precisely because the shell cannot clip).
   */
  topEdge: string;
  /**
   * Hover tint for the container border, split across the x sides and the
   * bottom rather than written as a single `hover:border-*`.
   *
   * This is load bearing: `border-x-*` compiles to border-left/right-color only
   * and `border-b-*` to border-bottom-color only, so neither can repaint the
   * accent top edge. A plain `hover:border-*` sets all four sides, and Tailwind
   * emits `hover:` variants *after* the base utilities, so it would win over
   * `border-t-*` and the card would lose its identity edge on hover.
   */
  hoverBorder: string;
  toggleOn: string;
  tag: string;
  interactive: string;
  iconChip: string;
}

const SPECS = {
  primary: {
    topEdge: 'border-t-primary-600 dark:border-t-primary-400',
    hoverBorder:
      'hover:border-x-primary-300 hover:border-b-primary-300 dark:hover:border-x-primary-500 dark:hover:border-b-primary-500',
    toggleOn: 'bg-blue-600 dark:bg-blue-500',
    tag: 'bg-primary-50 text-primary-700 dark:bg-primary-400/15 dark:text-primary-200',
    interactive:
      'text-primary-600 hover:text-primary-700 hover:bg-primary-50 dark:text-primary-300 dark:hover:text-primary-200 dark:hover:bg-primary-400/15',
    iconChip: 'bg-primary-50 dark:bg-primary-400/15',
  },
  cyan: {
    topEdge: 'border-t-cyan-600 dark:border-t-cyan-400',
    hoverBorder:
      'hover:border-x-cyan-300 hover:border-b-cyan-300 dark:hover:border-x-cyan-500 dark:hover:border-b-cyan-500',
    toggleOn: 'bg-cyan-600 dark:bg-cyan-500',
    tag: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-400/15 dark:text-cyan-200',
    interactive:
      'text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 dark:text-cyan-300 dark:hover:text-cyan-200 dark:hover:bg-cyan-400/15',
    iconChip: 'bg-cyan-50 dark:bg-cyan-400/15',
  },
  amber: {
    // Amber's mid stops fail contrast on white, so text sits at 700/800 while
    // the edge stays at 500 where it is read as a color, not as text.
    topEdge: 'border-t-amber-500 dark:border-t-amber-400',
    hoverBorder:
      'hover:border-x-amber-300 hover:border-b-amber-300 dark:hover:border-x-amber-500 dark:hover:border-b-amber-500',
    toggleOn: 'bg-amber-500',
    tag: 'bg-amber-50 text-amber-800 dark:bg-amber-400/15 dark:text-amber-200',
    interactive:
      'text-amber-700 hover:text-amber-800 hover:bg-amber-50 dark:text-amber-300 dark:hover:text-amber-200 dark:hover:bg-amber-400/15',
    iconChip: 'bg-amber-50 dark:bg-amber-400/15',
  },
  teal: {
    topEdge: 'border-t-teal-600 dark:border-t-teal-400',
    hoverBorder:
      'hover:border-x-teal-300 hover:border-b-teal-300 dark:hover:border-x-teal-500 dark:hover:border-b-teal-500',
    toggleOn: 'bg-teal-600 dark:bg-teal-500',
    tag: 'bg-teal-50 text-teal-700 dark:bg-teal-400/15 dark:text-teal-200',
    interactive:
      'text-teal-600 hover:text-teal-700 hover:bg-teal-50 dark:text-teal-300 dark:hover:text-teal-200 dark:hover:bg-teal-400/15',
    iconChip: 'bg-teal-50 dark:bg-teal-400/15',
  },
  neutral: {
    // Custom entities have no brand color, so their identity mark is grey and
    // their chips come straight from Semi's neutral fills.
    // The toggle is the exception: an *enabled* switch has to look active, so
    // it uses the brand color rather than grey. This accent used to be blue
    // throughout, which now collides with Semi's blue `info` Banners and Tags.
    topEdge: 'border-t-gray-300 dark:border-t-gray-600',
    hoverBorder:
      'hover:border-x-gray-300 hover:border-b-gray-300 dark:hover:border-x-gray-500 dark:hover:border-b-gray-500',
    toggleOn: 'bg-blue-600 dark:bg-blue-500',
    tag: 'bg-semi-color-fill-0 text-semi-color-text-1',
    interactive:
      'text-semi-color-primary hover:text-semi-color-primary-hover hover:bg-semi-color-primary-light-default',
    iconChip: 'bg-semi-color-fill-0',
  },
} satisfies Record<string, AccentSpec>;

export type AccentToken = keyof typeof SPECS;

export const ACCENTS = Object.fromEntries(
  Object.entries(SPECS).map(([token, spec]) => [
    token,
    {
      container: `${SURFACE} border-t-2 ${spec.topEdge} ${spec.hoverBorder}`,
      footer: FOOTER,
      divider: DIVIDER,
      toggleOn: spec.toggleOn,
      tag: spec.tag,
      interactive: spec.interactive,
      iconChip: spec.iconChip,
    },
  ]),
) as Record<AccentToken, AccentClasses>;

/**
 * Maps a registry entity type to its accent token, so every card of a given
 * type renders the same color-coded identity:
 *   server → primary (brand purple)    agent → cyan
 *   skill → amber                      virtualServer → teal
 *   custom → neutral
 *
 * Custom entity types share the neutral accent (they have no fixed brand color).
 */
export const ENTITY_ACCENTS = {
  server: 'primary',
  agent: 'cyan',
  skill: 'amber',
  virtualServer: 'teal',
  custom: 'neutral',
} as const satisfies Record<string, AccentToken>;
